import { useEffect, useState, useMemo } from 'react';
import { usePageTitle } from '@/hooks/use-page-title';
import { useCapabilityList } from '@/hooks/use-entity-list';
import { useHeaderAction } from '@/lib/header-action-context';
import { type Capability, capabilityDisplayTitle } from '@/types/capability-types';
import { getCapabilityColors, capabilityTitleIconBackground } from '@/lib/capability-colors';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Cable,
    RefreshCw,
    Trash2,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    Clock,
    Activity,
    Puzzle,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { capabilityDetailPath } from '@/types/capability-types';
import { cn } from '@/lib/utils';

const SORT_OPTIONS = [
    { key: 'name', label: 'Name' },
    { key: 'status', label: 'Status' },
    { key: 'auth', label: 'Auth Type' },
    { key: 'connected', label: 'Connected' },
];

const STATUS_FILTERS = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
    { value: 'error', label: 'Error' },
    { value: 'revoked', label: 'Revoked' },
    { value: 'pending', label: 'Pending' },
];

function extractConnectionMeta(item: Capability) {
    const tc = (item.config ?? item.ext ?? {}) as Record<string, unknown>;
    return {
        integrationRef: tc.integration_ref as string | undefined,
        authType: tc.auth_type as string | undefined,
        connStatus: (tc.status as string) ?? item.status,
        metadata: tc.metadata as Record<string, string> | undefined,
        owner: tc.owner as Record<string, string> | undefined,
    };
}

function StatusIndicator({ status }: { status?: string }) {
    switch (status) {
        case 'active':
            return (
                <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                    <span className="text-xs text-green-700 dark:text-green-400">Active</span>
                </div>
            );
        case 'expired':
            return (
                <div className="flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />
                    <span className="text-xs text-yellow-700 dark:text-yellow-400">Expired</span>
                </div>
            );
        case 'error':
            return (
                <div className="flex items-center gap-1.5">
                    <XCircle className="h-3.5 w-3.5 text-red-500" />
                    <span className="text-xs text-red-700 dark:text-red-400">Error</span>
                </div>
            );
        case 'revoked':
            return (
                <div className="flex items-center gap-1.5">
                    <XCircle className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-xs text-slate-500">Revoked</span>
                </div>
            );
        case 'pending':
            return (
                <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-blue-500" />
                    <span className="text-xs text-blue-700 dark:text-blue-400">Pending</span>
                </div>
            );
        default:
            return (
                <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">{status ?? 'Unknown'}</span>
                </div>
            );
    }
}

const headerPrimaryButtonClass =
    'inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';

function formatDate(dateStr?: string): string {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

export default function ConnectionsListPage() {
    usePageTitle('Connections');
    const { setHeaderTitle, setHeaderAction } = useHeaderAction();
    const titleIcon = useMemo(() => {
        const colors = getCapabilityColors('connection');
        const iconSurfaceStyle = capabilityTitleIconBackground(colors.hex, true);
        return (
            <div
                className={`flex h-7 w-7 items-center justify-center rounded-md ${colors.text}`}
                style={iconSurfaceStyle}
            >
                <Cable size={16} />
            </div>
        );
    }, []);

    useEffect(() => {
        setHeaderTitle({
            icon: titleIcon,
            title: 'Connections',
            subtitle: 'Browse and manage your active connections to external services.',
            intense: true,
        });
        setHeaderAction(
            <div className="ml-auto flex items-center gap-2">
                <Link to="/integrations" className={headerPrimaryButtonClass}>
                    <Puzzle size={14} />
                    Browse Integrations
                </Link>
            </div>,
        );
        return () => {
            setHeaderTitle(null);
            setHeaderAction(null);
        };
    }, [setHeaderTitle, setHeaderAction, titleIcon]);

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const { data, isLoading, error } = useCapabilityList(undefined, { type: 'connection' });

    const allConnections = useMemo(() => {
        const items = ((data as { items?: Capability[] })?.items ?? (Array.isArray(data) ? data : [])) as Capability[];
        return items;
    }, [data]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const connections = useMemo(() => {
        const filtered = allConnections.filter((item) => {
            const meta = extractConnectionMeta(item);
            const matchesSearch =
                !searchQuery ||
                item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                meta.integrationRef?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                meta.metadata?.workspace_name?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesStatus =
                statusFilter === 'all' || meta.connStatus === statusFilter;
            return matchesSearch && matchesStatus;
        });

        const sorted = [...filtered].sort((a, b) => {
            const metaA = extractConnectionMeta(a);
            const metaB = extractConnectionMeta(b);
            let cmp = 0;
            switch (sortField) {
                case 'name':
                    cmp = (metaA.integrationRef ?? a.name ?? '').localeCompare(metaB.integrationRef ?? b.name ?? '');
                    break;
                case 'status':
                    cmp = (metaA.connStatus ?? '').localeCompare(metaB.connStatus ?? '');
                    break;
                case 'auth':
                    cmp = (metaA.authType ?? '').localeCompare(metaB.authType ?? '');
                    break;
                case 'connected':
                    cmp = new Date(metaA.metadata?.connected_at ?? a.created ?? 0).getTime()
                        - new Date(metaB.metadata?.connected_at ?? b.created ?? 0).getTime();
                    break;
                default:
                    cmp = (a.name ?? '').localeCompare(b.name ?? '');
                    break;
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        });

        return sorted;
    }, [allConnections, searchQuery, statusFilter, sortField, sortDirection]);

    const statusCounts = useMemo(() => {
        const counts = { active: 0, expired: 0, error: 0, revoked: 0, pending: 0, total: 0 };
        for (const item of allConnections) {
            counts.total++;
            const s = extractConnectionMeta(item).connStatus ?? 'active';
            if (s in counts) (counts as Record<string, number>)[s]++;
        }
        return counts;
    }, [allConnections]);

    const navigate = useNavigate();

    const handleRefresh = (connName: string) => {
        toast.info(`Refreshing token for ${connName}...`);
    };

    const handleRevoke = (connName: string) => {
        toast.warning(`Revoking connection ${connName}...`);
    };

    const extraToolbar = (
        <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className={cn('w-[180px]', listControlsGlassTrigger)}>
                <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
                {STATUS_FILTERS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                        {s.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
            {!isLoading && statusCounts.total > 0 && (
                <div className="grid grid-cols-2 gap-3 px-6 pt-6 sm:grid-cols-4">
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                            <div>
                                <p className="text-2xl font-bold">{statusCounts.active}</p>
                                <p className="text-xs text-muted-foreground">Active</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            <div>
                                <p className="text-2xl font-bold">{statusCounts.expired}</p>
                                <p className="text-xs text-muted-foreground">Expired</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <XCircle className="h-5 w-5 text-red-500" />
                            <div>
                                <p className="text-2xl font-bold">{statusCounts.error}</p>
                                <p className="text-xs text-muted-foreground">Error</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <Activity className="h-5 w-5 text-blue-500" />
                            <div>
                                <p className="text-2xl font-bold">{statusCounts.total}</p>
                                <p className="text-xs text-muted-foreground">Total</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
            {error ? (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                    Failed to load connections. Please try again.
                </div>
            ) : (
                <CapabilityListPanel<Capability>
                    items={connections.length > 0 ? [connections[0]] : []}
                    sortOptions={SORT_OPTIONS}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    searchPlaceholder="Search connections..."
                    allTags={[]}
                    selectedTags={new Set<string>()}
                    onToggleTag={() => {}}
                    onClearAllTags={() => {}}
                    onSelectAllTags={() => {}}
                    showTagsFilter={false}
                    extraToolbar={extraToolbar}
                    isLoading={isLoading}
                    loadingMessage="Loading connections..."
                    emptyStateVariant="glass"
                    emptyMessage={
                        searchQuery || statusFilter !== 'all'
                            ? 'No connections match your filters.'
                            : "You haven't connected any integrations yet."
                    }
                    gridClassName="grid grid-cols-1"
                    renderCard={() => {
                        return (
                            <div key="connections-table" className="rounded-md border bg-white">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Integration</TableHead>
                                            <TableHead>Account</TableHead>
                                            <TableHead>Auth Type</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Connected</TableHead>
                                            <TableHead>Last Used</TableHead>
                                            <TableHead className="w-[100px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {connections.map((conn) => {
                                            const meta = extractConnectionMeta(conn);
                                            return (
                                                <TableRow
                                                    key={conn.name}
                                                    className="cursor-pointer hover:bg-slate-50"
                                                    onClick={() => navigate(capabilityDetailPath(conn.app_key, 'connection', conn.name))}
                                                >
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <Cable className="h-4 w-4 text-muted-foreground" />
                                                            <div>
                                                                <p className="text-sm font-medium">
                                                                    {meta.integrationRef ?? capabilityDisplayTitle(conn)}
                                                                </p>
                                                                <p className="max-w-[200px] truncate text-xs text-muted-foreground">
                                                                    {conn.name}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm">
                                                            {meta.metadata?.workspace_name ??
                                                                meta.metadata?.account_name ??
                                                                '—'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className="text-[10px]">
                                                            {meta.authType ?? '—'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <StatusIndicator status={meta.connStatus} />
                                                    </TableCell>
                                                    <TableCell className="text-xs text-muted-foreground">
                                                        {formatDate(meta.metadata?.connected_at ?? conn.created)}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-muted-foreground">
                                                        {formatDate(meta.metadata?.last_used_at)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7"
                                                                title="Refresh token"
                                                                onClick={(e) => { e.stopPropagation(); handleRefresh(conn.name); }}
                                                            >
                                                                <RefreshCw className="h-3.5 w-3.5" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 text-destructive hover:text-destructive"
                                                                title="Revoke connection"
                                                                onClick={(e) => { e.stopPropagation(); handleRevoke(conn.name); }}
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        );
                    }}
                />
            )}
        </div>
    );
}
